import React, { useState, useContext, useEffect, useRef } from 'react';
import { CartContext } from '../contexts/cardContext';
import defImage from '../images/defImage_dark.png';
import Navigation from './Navigation';
import { sortMenuGroups } from '../utils/menuOrder';

function Products({ groups, products }) {
  const MAX_MODIFIERS_PER_PRODUCT = 12;
  const [showPopup, setShowPopup] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [progress, setProgress] = useState(0);
  const { addToCart, cart, decreaseItemAmount, removeItem } = useContext(CartContext);
  const hiddenProducts = ['коробка для пиццы 30 см', 'коробка для пиццы 40 см'];
  const { updatePizzaBoxes } = useContext(CartContext);
  const [currentSection, setCurrentSection] = useState(null);
  const sectionsRef = useRef([]);
  const navigationRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const sectionOffsets = sectionsRef.current
        .filter(Boolean)
        .map((section) => ({
          id: section.id,
          offsetTop: section.offsetTop,
        }));

      if (sectionOffsets.length === 0) {
        setCurrentSection(null);
        return;
      }

      const scrollPosition = window.scrollY;
      const activeSection = sectionOffsets.find((section, index) => {
        const nextSection = sectionOffsets[index + 1];
        return nextSection
          ? scrollPosition >= section.offsetTop && scrollPosition < nextSection.offsetTop
          : scrollPosition >= section.offsetTop;
      });
      if (activeSection?.id !== currentSection) {
        setCurrentSection(activeSection?.id || null);
        const activeNavElement = navigationRef.current?.querySelector(`[data-id="${activeSection?.id}"]`);
        if (activeNavElement) {
          activeNavElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }
    };
  
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentSection]);

  const handleSectionChange = (groupId) => {
    const section = document.getElementById(groupId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (groups && products) {
      const pizzaBoxes = findPizzaBoxes(products);
      updatePizzaBoxes(pizzaBoxes);
    }
  }, []);

  useEffect(() => {
    if (!groups || !products) {
      const interval = setInterval(() => {
        setProgress((prev) => (prev < 100 ? prev + 0.5 : 100));
      }, 100);
      return () => clearInterval(interval);
    }
  }, [groups, products]);
  if (!groups || !products) {
    return (
      <div className="progress">
        <p className="progress__text">Меню скоро загрузится</p>
        <div className="progress__bar">
          <div
            className="progress__bar-fill"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
    );
  }

  const sortedGroups = sortMenuGroups(groups);
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const isSearchActive = normalizedSearchQuery.length > 0;

  const findPizzaBoxes = (products) => {
    const pizzaBox30 = products.find(product => product.name === 'коробка для пиццы 30 см');
    const pizzaBox40 = products.find(product => product.name === 'коробка для пиццы 40 см');
    return { pizzaBox30, pizzaBox40 };
  };

  const isVisibleProduct = (product) => {
    if (!product.isIncludedInMenu || product.isDeleted) {
      return false;
    }
    if (product.paymentSubject === "УСЛУГА") {
      return false;
    }
    if (hiddenProducts.includes(product.name)) {
      return false;
    }

    return true;
  };

  const normalizeSearchText = (text = '') =>
    text
      .toLowerCase()
      .replace(/ё/g, 'е')
      .replace(/\s+/g, ' ')
      .trim();

  const getSearchScore = (product, query) => {
    const normalizedQuery = normalizeSearchText(query);
    if (!normalizedQuery) {
      return 0;
    }

    const normalizedName = normalizeSearchText(product.name || '');
    const normalizedDescription = normalizeSearchText(product.description || '');
    const nameWords = normalizedName.split(' ').filter(Boolean);
    let score = 0;

    if (normalizedName === normalizedQuery) {
      score += 1000;
    }
    if (normalizedName.startsWith(normalizedQuery)) {
      score += 700;
    }
    if (nameWords.some((word) => word.startsWith(normalizedQuery))) {
      score += 500;
    }
    if (normalizedName.includes(normalizedQuery)) {
      score += 300;
    }
    if (normalizedDescription.includes(normalizedQuery)) {
      score += 80;
    }

    return score;
  };

  const searchResults = products
    .filter((product) => isVisibleProduct(product))
    .map((product) => ({
      product,
      score: getSearchScore(product, normalizedSearchQuery),
    }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ product }) => product);

  const generateItemKey = (id, modifiers = []) => {
    const sortedModifiers = modifiers.map((mod) => mod.id).sort();
    return `${id}-${sortedModifiers.join('-')}`;
  };

  const openPopup = (product) => {
    setSelectedProduct(product);
    setSelectedModifiers({});
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  const handleCategoryClick = (groupId, parentId) => {
    setSelectedCategory((prev) => ({
      ...prev,
      [parentId]: groupId,
    }));
  };

  const findModifierById = (modifierId) => {
    const modifier = products.find(
      (product) => product.id === modifierId && product.type === 'Modifier'
    );
    if (modifier) {
      const modifierPrice = modifier.price || 0;
      return { 
        name: modifier.name, 
        groupId: modifier.groupId,
        price: modifierPrice 
      };
    } else {
      return null;
    }
  };

  const getSelectedModifiersCount = (modifiers = selectedModifiers) =>
    Object.values(modifiers).filter((modifier) => modifier !== null).length;

  const getSauceGroupId = () => {
    const sauceGroup = groups.find(group => group.name === 'Соус на выбор');
    return sauceGroup ? sauceGroup.id : null;
  };

  const handleModifierChange = (modifierId, price, checked, groupId) => {
    setSelectedModifiers((prev) => {
      const groupSouceId = getSauceGroupId();
      if (groupId === groupSouceId && checked) {
        return {
          [modifierId]: { price, groupId },
        };
      }

      if (checked && !prev[modifierId] && getSelectedModifiersCount(prev) >= MAX_MODIFIERS_PER_PRODUCT) {
        return prev;
      }

      return {
        ...prev,
        [modifierId]: checked ? { price, groupId } : null,
      };
    });
  };

  const handleAddToCart = () => {
    const selectedModifiersList = Object.entries(selectedModifiers)
      .filter(([, value]) => value !== null)
      .slice(0, MAX_MODIFIERS_PER_PRODUCT)
      .map(([modifierId, { price, groupId }]) => {
        const modifier = findModifierById(modifierId);
        return {
          id: modifierId,
          name: modifier?.name || "Неизвестный модификатор",
          price,
          amount: 1,
          productGroupId: groupId,
        };
      });
    const itemKey = generateItemKey(selectedProduct.id, selectedModifiersList);
    addToCart({
      key: itemKey,
      ...selectedProduct,
      totalPrice: getTotalPrice(),
      modifiers: selectedModifiersList,
      amount: 1,
    });
    setSelectedModifiers({});
  };

  const getTotalPrice = () => {
    const modifiersTotal = Object.values(selectedModifiers)
      .filter((modifier) => modifier !== null)
      .reduce((sum, { price }) => sum + price, 0);
    return selectedProduct.price + modifiersTotal;
  };

  const selectedModifiersCount = getSelectedModifiersCount();
  const isModifiersLimitReached = selectedModifiersCount >= MAX_MODIFIERS_PER_PRODUCT;

  const getCartItemAmount = (product) => {
    const uniqueKey = generateItemKey(product.id, product.modifiers || []);
    const cartItem = cart.find((item) => item.key === uniqueKey);
    return cartItem ? cartItem.amount : 0;
  };

  const getCartItemAmountWithModifiers = (product, hasModifiers) => {
    return cart
      .filter((item) => item.productId === product.id && !!item.modifiers.length === hasModifiers)
      .reduce((sum, item) => sum + item.amount, 0);
  };

  const getCartItemTotalPriceWithModifiers = (product) => {
    return cart
      .filter((item) => item.productId === product.id && item.modifiers.length > 0)
      .reduce((sum, item) => {
        const modifiersTotal = item.modifiers.reduce((modSum, mod) => modSum + mod.price, 0);
        return sum + (product.price + modifiersTotal) * item.amount;
      }, 0);
  };

  const renderProductCard = (product) => {
    const amountInCart = getCartItemAmount(product);

    return (
      <div key={product.id} className="product">
        <img
          loading="lazy"
          src={product.image || defImage}
          alt={product.name}
          className="product__image"
          onClick={() => openPopup(product)}
        />
        <h3 className="product__name">{product.name}</h3>
        <div className="product__buttons">
          {amountInCart > 0 && (
            <button
              className="product__button-minus"
              onClick={() => decreaseItemAmount(generateItemKey(product.id, product.modifiers || []))}
            >
            </button>
          )}
          <button
            className="product__button-add"
            onClick={() => addToCart(product, products)}
          >
            {product.price} р {amountInCart > 0 && `x ${amountInCart}`}
          </button>
          {amountInCart > 0 && (
            <button
              className="product__button-remove"
              onClick={() => removeItem(generateItemKey(product.id, product.modifiers || []))}
            >
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className='menu'>
        <div className="menu__search">
          <div className="menu__search-field">
            <span className="menu__search-icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="menu__search-icon-svg"
              >
                <circle cx="11" cy="11" r="6.5" />
                <path d="M16 16L20 20" />
              </svg>
            </span>
            <input
              id="menu-search"
              type="text"
              className="menu__search-input"
              placeholder="Начните вводить название блюда"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {isSearchActive && (
              <button
                type="button"
                className="menu__search-clear"
                onClick={() => setSearchQuery('')}
                aria-label="Очистить поиск"
              >
                ×
              </button>
            )}
          </div>
        </div>
        {!isSearchActive && (
          <Navigation
            ref={navigationRef}
            groups={sortedGroups}
            activeGroup={currentSection}
            onSectionChange={handleSectionChange}
          />
        )}
        {isSearchActive ? (
          <div className="menu__search-results">
            <div className="menu__search-summary">
              {searchResults.length > 0
                ? `Найдено блюд: ${searchResults.length}`
                : 'Ничего не найдено, попробуйте другое название'}
            </div>
            {searchResults.length > 0 && (
              <div className="products">
                {searchResults.map((product) => renderProductCard(product))}
              </div>
            )}
          </div>
        ) : (
          sortedGroups.filter(group => group.parentGroup === null && !group.isGroupModifier).map((group, index) => {
          const subGroups = sortMenuGroups(
            groups.filter(subGroup => subGroup.parentGroup === group.id)
          );
          if (group.name.toLowerCase().includes("доставка")) {
            return null;
          }  
          return (
            <div
              key={group.id}
              id={group.id}
              ref={(el) => (sectionsRef.current[index] = el)}
              className="menu__box"
            >
              {group.name && !group.name.toLowerCase().includes("доставка") && (
                <h2 className='menu__title'>{group.name}</h2>
              )}
              {subGroups.length > 0 && !subGroups.some(subGroup => subGroup.name.toLowerCase().includes("невьянск")) && (
                <div className="menu__categories">
                  <button
                    onClick={() => handleCategoryClick(null, group.id)}
                    className="menu__category-button"
                  >
                    Все
                  </button>
                  {subGroups.map(subGroup => (
                    <button
                      key={subGroup.id}
                      onClick={() => handleCategoryClick(subGroup.id, group.id)}
                      className="menu__category-button"
                    >
                      {subGroup.name}
                    </button>
                  ))}
                </div>
              )}
              <div className="products">
                {products.filter(product => {
                  if (!isVisibleProduct(product)) {
                    return false;
                  }
                  const selectedSubCategory = selectedCategory[group.id];
                    if (selectedSubCategory) {
                      return product.parentGroup === selectedSubCategory;
                    }
                  return product.parentGroup === group.id ||
                    sortedGroups.some(subGroup => subGroup.parentGroup === group.id &&
                                            subGroup.id === product.parentGroup);
                }).map((product) => renderProductCard(product))}
            </div>
          </div>
        );
      }))}
      {showPopup && selectedProduct && (
          <div className="popup__overlay" onClick={closePopup}>
            <div className='product-popup' onClick={(e) => e.stopPropagation()}>
            <button className="product-popup__close-button" onClick={closePopup}></button>
            <img className="product-popup__image" src={selectedProduct.image || defImage} alt={selectedProduct.name} />
            <div className="product-popup__description">
              <div className="">
                <h2 className="product-popup__heading">{selectedProduct.name}</h2>
                <p className="product-popup__about">{selectedProduct.description}</p>
                <h3>В одной порции на {selectedProduct.weight} гр: </h3>
                <ul className='product-popup__amount'>
                  <li className='product-popup__amount-li'>белки</li>
                  <li className='product-popup__amount-li'>жиры</li>
                  <li className='product-popup__amount-li'>углеводы</li>
                  <li className='product-popup__amount-li'>ккал</li>
                  <li className='product-popup__amount-li'>{selectedProduct.proteinsFullAmount}</li>
                  <li className='product-popup__amount-li'>{selectedProduct.fatFullAmount}</li>
                  <li className='product-popup__amount-li'>{selectedProduct.carbohydratesFullAmount}</li>
                  <li className='product-popup__amount-li'>{selectedProduct.energyFullAmount}</li>
                </ul>
                {selectedProduct.groupModifiers && selectedProduct.groupModifiers.length > 0 ? (
                  <div className=''>
                    <h3>Добавить ингредиент:</h3>
                    <p className="product-popup__modifier-limit">
                      Выбрано: {selectedModifiersCount} из {MAX_MODIFIERS_PER_PRODUCT}
                      {isModifiersLimitReached ? '. Максимум добавок выбран.' : ''}
                    </p>
                    <ul className='product-popup__modificator'>
                      {selectedProduct.groupModifiers.map(groupModifier => (
                        <li className='product-popup__modificator-li' key={groupModifier.id}>
                          {groupModifier.childModifiers && groupModifier.childModifiers.length > 0 ? (
                            groupModifier.childModifiers.map(childModifier => {
                              const modifier = findModifierById(childModifier.id);
                              const isChecked = !!selectedModifiers[childModifier.id];
                              const isDisabled = isModifiersLimitReached && !isChecked;
                              return (
                                <div key={childModifier.id}>
                                  <label
                                    onClick={() => !isDisabled && handleModifierChange(
                                      childModifier.id,
                                      modifier?.price || 0,
                                      !isChecked,
                                      modifier?.groupId
                                    )}
                                    className={[
                                      isChecked ? 'product-popup__modifier-checked' : 'product-popup__modifier-unchecked',
                                      isDisabled ? 'product-popup__modifier-disabled' : ''
                                    ].filter(Boolean).join(' ')}
                                  >
                                    {modifier ? modifier.name : "Модификатор не найден"}
                                  </label>
                                </div>
                              );
                            })
                          ) : (
                            <p>Нет модификаторов</p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p></p>
                )}
              </div>
              <div>
                <h3>Цена: {getTotalPrice()} р</h3>
                {selectedProduct.groupModifiers && selectedProduct.groupModifiers.length > 0 ? (
                  <div>
                    <p>Добавлено: {getCartItemAmountWithModifiers(selectedProduct, false)} шт. - {getCartItemAmountWithModifiers(selectedProduct, false) * selectedProduct.price} руб.</p>
                    <p>С дополнительными ингридиентами: {getCartItemAmountWithModifiers(selectedProduct, true)} шт. - {getCartItemTotalPriceWithModifiers(selectedProduct)} руб.</p>
                  </div>
                ) : (
                  <p>В корзине: {getCartItemAmount(selectedProduct)} шт. - общая цена: {getCartItemAmount(selectedProduct) * getTotalPrice()} руб.</p>
                )}
              </div>
              <div className="product__buttons">
                {getCartItemAmount(selectedProduct) > 0 && (
                  <button
                    className="product__button-minus"
                    onClick={() => decreaseItemAmount(generateItemKey(selectedProduct.id, Object.keys(selectedModifiers)))}
                  >
                  </button>
                )}
                <button
                  className="product__button-add"
                  onClick={handleAddToCart}
                >
                  Добавить
                </button>
                {getCartItemAmount(selectedProduct) > 0 && (
                  <button
                    className="product__button-remove"
                    onClick={() => {
                      removeItem(`${selectedProduct.id}-${Object.keys(selectedModifiers).join('-')}`);
                      closePopup();
                    }}
                  >
                  </button>
                )}
              </div>
            </div>
            </div>
          </div>
      )}
    </div>
    </>
  );
}

export default Products;
